import { APP_CURRENCY } from '../constants/currency';
import { formatDecimal, parseLocaleDecimal } from '../localeNumber';

// Formateadores de dinero centralizados
export const formatCurrency = (amount: number | string | null | undefined): string => {
  if (amount === null || amount === undefined || amount === '') return '';

  try {
    const numericAmount = typeof amount === 'string' ? parseFloat(amount) : Number(amount);

    if (isNaN(numericAmount)) return '';

    return new Intl.NumberFormat(APP_CURRENCY.locale, {
      style: 'currency',
      currency: APP_CURRENCY.code,
      minimumFractionDigits: APP_CURRENCY.fractionDigits,
      maximumFractionDigits: APP_CURRENCY.fractionDigits,
    }).format(numericAmount);
  } catch (error) {
    console.warn('Error formatting currency:', error);
    return '';
  }
};

/** Precio de venta por pieza en tablas de inventario; muestra "—" si no hay precio (> 0). */
export const formatPieceSalePrice = (price: number | null | undefined): string => {
  if (price == null || !Number.isFinite(price) || price <= 0) return '—';
  return formatCurrency(price);
};

/** Estilo de celda para precio de venta en tablas de inventario (PO: verde como catálogo). */
export const pieceSalePriceTableCellSx = {
  color: 'success.main',
  fontWeight: 600,
} as const;

export const formatNumber = (
  number: number | string | null | undefined,
  decimals: number = 2,
): string => {
  return formatDecimal(number, decimals);
};

export const formatPercentage = (
  value: number | string | null | undefined,
  decimals: number = 1,
): string => {
  if (value === null || value === undefined || value === '') return '';

  try {
    const numericValue = typeof value === 'string' ? parseFloat(value) : Number(value);

    if (isNaN(numericValue)) return '';

    return new Intl.NumberFormat('es-ES', {
      style: 'percent',
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals,
    }).format(numericValue / 100);
  } catch (error) {
    console.warn('Error formatting percentage:', error);
    return '';
  }
};

/**
 * Display for markup/discount: % with Bs equivalent, or Bs with % equivalent.
 * Example: `10% (Bs 50,00)` or `Bs 50,00 (10%)`.
 */
export const formatPriceAdjustmentDisplay = (
  adjustmentType: string,
  adjustmentValue: number,
  basePrice: number,
): string => {
  if (adjustmentType === 'PERCENTAGE') {
    const equivalentAmount = roundMoney((basePrice * adjustmentValue) / 100);
    return `${adjustmentValue}% (${formatCurrency(equivalentAmount)})`;
  }

  const equivalentPercentage = basePrice > 0 ? roundMoney((adjustmentValue / basePrice) * 100) : 0;
  return `${formatCurrency(adjustmentValue)} (${equivalentPercentage}%)`;
};

export const formatCompactNumber = (number: number | string | null | undefined): string => {
  if (number === null || number === undefined || number === '') return '';

  try {
    const numericNumber = typeof number === 'string' ? parseFloat(number) : Number(number);

    if (isNaN(numericNumber)) return '';

    return new Intl.NumberFormat('es-ES', {
      notation: 'compact',
      maximumFractionDigits: 1,
    }).format(numericNumber);
  } catch (error) {
    console.warn('Error formatting compact number:', error);
    return '';
  }
};

export const parseMoney = (value: string): number => {
  if (!value) return 0;

  try {
    const parsed = parseLocaleDecimal(value);
    return parsed ?? 0;
  } catch (error) {
    console.warn('Error parsing money:', error);
    return 0;
  }
};

export const roundMoney = (value: number | string | null | undefined): number => {
  if (value === null || value === undefined || value === '') return 0;

  try {
    const numericValue = typeof value === 'string' ? parseFloat(value) : Number(value);

    if (isNaN(numericValue)) return 0;

    return Math.round(numericValue * 100) / 100;
  } catch (error) {
    console.warn('Error rounding money:', error);
    return 0;
  }
};

export const clampMoney = (
  value: number | string | null | undefined,
  min: number = 0,
  max: number = 999999999,
): number => {
  const numericValue = roundMoney(value);
  return Math.min(Math.max(numericValue, min), max);
};
